import React, { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  useFetchPrintFacetsQuery,
  useLazyResolvePrintBarcodeQuery,
  useResolvePrintItemsMutation,
} from "../../../api/printingApi";
import { Btn, EmptyState, Icon, Select, Spinner, cx } from "../../catalogProductStudio/edit/StudioUi";
import { isSelectableItem } from "../state/printSelectionModel";
import { fill, plural } from "../printCenterModel";
import PrintProductCard from "./PrintProductCard";
import usePrintCatalog, { useDebouncedValue } from "./usePrintCatalog";

// Filters live in the URL so they survive navigation and can be shared. The
// selection does not depend on them in any way.
const FILTER_KEYS = ["q", "type", "brand", "category", "color", "size", "status", "stock", "needs", "barcode", "received", "created", "location", "sort"];
const SORTS = ["recently_received", "labels_needed", "newest", "name", "stock"];

const readFilters = (params) => Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) || ""]));

const toApiFilters = (filters, search) => ({
  search: search || undefined,
  productFamily: filters.type || undefined,
  brandId: filters.brand || undefined,
  categoryId: filters.category || undefined,
  color: filters.color || undefined,
  size: filters.size || undefined,
  status: filters.status || undefined,
  stock: filters.stock || undefined,
  needsLabels: filters.needs === "1" ? true : undefined,
  barcode: filters.barcode || undefined,
  receivedWithinDays: filters.received || undefined,
  createdWithinDays: filters.created || undefined,
  locationId: filters.location || undefined,
});

function FilterSelect({ id, label, value, onChange, children }) {
  return (
    <label className="pc-filter" htmlFor={id}>
      <span className="pc-filter__label">{label}</span>
      <Select id={id} className="pc-filter__select" value={value} onChange={onChange}>
        {children}
      </Select>
    </label>
  );
}

function PrintCatalogView({ tt, entries, onSelect, onDeselect, notify }) {
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const [searchDraft, setSearchDraft] = useState(filters.q);
  const debouncedSearch = useDebouncedValue(searchDraft, 300);
  const searchRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const apiParams = useMemo(
    () => ({ ...toApiFilters(filters, debouncedSearch.trim()), sort: filters.sort || "newest" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, debouncedSearch]
  );
  const catalog = usePrintCatalog(apiParams);
  const { data: facets } = useFetchPrintFacetsQuery();
  const [resolveBarcode, { isFetching: isResolvingBarcode }] = useLazyResolvePrintBarcodeQuery();
  const [resolveItems, { isLoading: isResolvingAll }] = useResolvePrintItemsMutation();

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(params);
    FILTER_KEYS.forEach((key) => next.delete(key));
    setSearchDraft("");
    setParams(next, { replace: true });
  };

  React.useEffect(() => {
    if ((params.get("q") || "") === debouncedSearch.trim()) return;
    setFilter("q", debouncedSearch.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Enter on an exact barcode (a scanner sends Enter) adds that label at once.
  const handleSearchKeyDown = async (event) => {
    if (event.key !== "Enter") return;
    const value = searchDraft.trim();
    if (!value) return;
    event.preventDefault();
    try {
      const result = await resolveBarcode(value).unwrap();
      if (result.item && isSelectableItem(result.item)) {
        onSelect([result.item], "scan");
        notify(fill(tt("scanAdded"), { name: `${result.item.productTitle} · ${result.item.barcode}` }), "success");
        setSearchDraft("");
        searchRef.current?.focus();
      } else if (result.match === "serialized_variant") {
        notify(tt("scanSerializedVariant"), "warning");
      } else if (result.match === "unit_not_in_stock") {
        notify(tt("scanUnitNotInStock"), "warning");
      } else if (result.item?.blockedReason) {
        notify(tt("blockedMissingBarcode"), "warning");
      }
    } catch (_error) {
      // Not a barcode: the typed text simply stays a search.
    }
  };

  const shownItems = catalog.products.flatMap((product) => product.items).filter(isSelectableItem);
  const shownUnselected = shownItems.filter((item) => !entries[item.key]);

  const selectAllMatching = async () => {
    try {
      const result = await resolveItems({ filters: toApiFilters(filters, debouncedSearch.trim()) }).unwrap();
      const selectable = (result.items || []).filter(isSelectableItem);
      onSelect(selectable);
      notify(
        result.truncated
          ? fill(tt("selectAllTruncated"), { count: selectable.length, total: result.total })
          : plural(tt, "selectAllAdded", selectable.length),
        result.truncated ? "warning" : "success"
      );
    } catch (_error) {
      notify(tt("loadError"), "danger");
    }
  };

  const activeChips = [
    filters.q && { key: "q", label: fill(tt("chipSearch"), { value: filters.q }) },
    filters.needs === "1" && { key: "needs", label: tt("filterNeedsLabels") },
    filters.stock && { key: "stock", label: tt(`filterStock_${filters.stock}`) },
    filters.barcode === "missing" && { key: "barcode", label: tt("filterMissingBarcode") },
    filters.type && { key: "type", label: tt(`filterType_${filters.type}`) },
    filters.brand && { key: "brand", label: facets?.brands?.find((b) => String(b.id) === filters.brand)?.name || filters.brand },
    filters.category && { key: "category", label: facets?.categories?.find((c) => String(c.id) === filters.category)?.name || filters.category },
    filters.color && { key: "color", label: filters.color },
    filters.size && { key: "size", label: fill(tt("chipSize"), { value: filters.size }) },
    filters.status && { key: "status", label: tt(`status_${filters.status}`) },
    filters.received && { key: "received", label: tt(`filterReceived_${filters.received}`) },
    filters.created && { key: "created", label: tt(`filterCreated_${filters.created}`) },
    filters.location && { key: "location", label: facets?.locations?.find((l) => String(l.id) === filters.location)?.name || filters.location },
  ].filter(Boolean);

  return (
    <div className="pc-catalog">
      <aside className={cx("pc-filters", filtersOpen && "is-open")} aria-label={tt("filtersTitle")}>
        <div className="pc-filters__head">
          <h3 className="pc-section-title">{tt("filtersTitle")}</h3>
          <button type="button" className="pse-icon-btn pc-filters__close" aria-label={tt("close")} onClick={() => setFiltersOpen(false)}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="pc-filters__toggles">
          {[
            ["needs", "1", "filterNeedsLabels", "tag"],
            ["stock", "in_stock", "filterStock_in_stock", "cube"],
            ["barcode", "missing", "filterMissingBarcode", "alert"],
          ].map(([key, value, labelKey, icon]) => (
            <button
              key={key}
              type="button"
              className={cx("pc-toggle-chip", filters[key] === value && "is-active")}
              aria-pressed={filters[key] === value}
              onClick={() => setFilter(key, filters[key] === value ? "" : value)}
            >
              <Icon name={icon} size={14} />
              {tt(labelKey)}
            </button>
          ))}
        </div>
        <FilterSelect id="pc-f-type" label={tt("filterType")} value={filters.type} onChange={(value) => setFilter("type", value)}>
          <option value="">{tt("filterAny")}</option>
          {["shoes", "textile", "accessories"].map((type) => (
            <option key={type} value={type}>{tt(`filterType_${type}`)}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-brand" label={tt("filterBrand")} value={filters.brand} onChange={(value) => setFilter("brand", value)}>
          <option value="">{tt("filterAny")}</option>
          {(facets?.brands || []).map((brand) => (
            <option key={brand.id} value={brand.id}>{brand.name}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-category" label={tt("filterCategory")} value={filters.category} onChange={(value) => setFilter("category", value)}>
          <option value="">{tt("filterAny")}</option>
          {(facets?.categories || []).map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-color" label={tt("filterColor")} value={filters.color} onChange={(value) => setFilter("color", value)}>
          <option value="">{tt("filterAny")}</option>
          {(facets?.colors || []).map((color) => (
            <option key={color} value={color}>{color}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-size" label={tt("filterSize")} value={filters.size} onChange={(value) => setFilter("size", value)}>
          <option value="">{tt("filterAny")}</option>
          {(facets?.sizes || []).map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-received" label={tt("filterReceived")} value={filters.received} onChange={(value) => setFilter("received", value)}>
          <option value="">{tt("filterAny")}</option>
          {["1", "7", "30"].map((days) => (
            <option key={days} value={days}>{tt(`filterReceived_${days}`)}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-created" label={tt("filterCreated")} value={filters.created} onChange={(value) => setFilter("created", value)}>
          <option value="">{tt("filterAny")}</option>
          {["7", "30"].map((days) => (
            <option key={days} value={days}>{tt(`filterCreated_${days}`)}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-status" label={tt("filterStatus")} value={filters.status} onChange={(value) => setFilter("status", value)}>
          <option value="">{tt("filterStatusLive")}</option>
          {["active", "draft", "archived"].map((status) => (
            <option key={status} value={status}>{tt(`status_${status}`)}</option>
          ))}
        </FilterSelect>
        <FilterSelect id="pc-f-stock" label={tt("filterStock")} value={filters.stock} onChange={(value) => setFilter("stock", value)}>
          <option value="">{tt("filterAny")}</option>
          {["in_stock", "out_of_stock"].map((stock) => (
            <option key={stock} value={stock}>{tt(`filterStock_${stock}`)}</option>
          ))}
        </FilterSelect>
        {(facets?.locations || []).length > 1 ? (
          <FilterSelect id="pc-f-location" label={tt("filterLocation")} value={filters.location} onChange={(value) => setFilter("location", value)}>
            <option value="">{tt("filterAllLocations")}</option>
            {facets.locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </FilterSelect>
        ) : null}
      </aside>

      <section className="pc-results" aria-label={tt("resultsTitle")}>
        <div className="pc-search">
          <Icon name="search" size={18} />
          <input
            ref={searchRef}
            type="search"
            className="pc-search__input"
            value={searchDraft}
            placeholder={tt("searchPlaceholder")}
            aria-label={tt("searchPlaceholder")}
            onChange={(event) => setSearchDraft(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {isResolvingBarcode ? <Spinner /> : <span className="pc-search__hint">{tt("searchScanHint")}</span>}
          <Btn size="sm" icon="sliders" className="pc-filters-open" onClick={() => setFiltersOpen(true)}>
            {tt("filtersTitle")}
          </Btn>
        </div>

        <div className="pc-results__bar">
          <div className="pc-results__count">
            <strong>{plural(tt, "productCount", catalog.total)}</strong>
            {catalog.isRefreshing ? <Spinner /> : null}
          </div>
          <label className="pc-sort">
            <span className="pc-muted pc-small">{tt("sortLabel")}</span>
            <Select value={filters.sort || "newest"} onChange={(value) => setFilter("sort", value === "newest" ? "" : value)}>
              {SORTS.map((sort) => (
                <option key={sort} value={sort}>{tt(`sort_${sort}`)}</option>
              ))}
            </Select>
          </label>
        </div>

        {activeChips.length ? (
          <div className="pc-active-filters">
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="pc-active-chip"
                onClick={() => {
                  if (chip.key === "q") setSearchDraft("");
                  setFilter(chip.key, "");
                }}
                aria-label={fill(tt("removeFilter"), { name: chip.label })}
              >
                {chip.label}
                <Icon name="x" size={12} />
              </button>
            ))}
            <button type="button" className="pc-link" onClick={clearFilters}>
              {tt("clearFilters")}
            </button>
          </div>
        ) : null}

        {catalog.total > 0 ? (
          <div className="pc-bulk">
            <Btn size="sm" icon="check" disabled={!shownUnselected.length} onClick={() => onSelect(shownUnselected)}>
              {plural(tt, "selectShown", shownUnselected.length)}
            </Btn>
            <Btn size="sm" icon="layers" disabled={isResolvingAll} onClick={selectAllMatching}>
              {plural(tt, "selectAllMatching", catalog.total)}
            </Btn>
          </div>
        ) : null}

        {catalog.isInitialLoading ? (
          <div className="pc-loading">
            <Spinner label={tt("loading")} />
          </div>
        ) : catalog.isError ? (
          <EmptyState icon="alert" title={tt("loadError")} action={<Btn onClick={catalog.refetch}>{tt("retry")}</Btn>} />
        ) : !catalog.products.length ? (
          <EmptyState
            icon="search"
            title={activeChips.length ? tt("noResultsFiltered") : tt("noResults")}
            action={activeChips.length ? <Btn onClick={clearFilters}>{tt("clearFilters")}</Btn> : null}
          />
        ) : (
          <div className={cx("pc-product-list", catalog.isRefreshing && "is-refreshing")}>
            {catalog.products.map((product) => (
              <PrintProductCard
                key={product.id}
                tt={tt}
                product={product}
                entries={entries}
                onSelect={onSelect}
                onDeselect={onDeselect}
                defaultExpanded={catalog.products.length <= 3 || Boolean(debouncedSearch.trim())}
              />
            ))}
            {catalog.hasMore ? (
              <div className="pc-load-more">
                <Btn onClick={catalog.loadMore} disabled={catalog.isFetchingMore}>
                  {catalog.isFetchingMore ? tt("loading") : tt("loadMore")}
                </Btn>
              </div>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}

export default PrintCatalogView;
